import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import {
  Button,
  colors,
  InputText,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeading,
  TableRow,
} from '@insurancemenu/imenu-components-lib';
import { Add, Delete, Done, NavigationClose } from '@insurancemenu/imenu-components-lib/dist/icons';
import { toJS } from 'mobx';
import { RateCap } from 'store/ui-stores/proposal-v2/customPlanRateCapStore';
import uniqid from 'uniqid';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { CustomFieldControl } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { createStyles } from './styles';

const PlanRateCap: React.FC = observer(() => {
  // Mobx store
  const { rateRFQModalStore, customPlanRateCapStore } = useRootStore();

  const convertedRateCaps = customPlanRateCapStore
    .getRateCaps()
    .map((rateCap) => toJS(rateCap))
    .slice();
  const [newRateCap, setNewRateCap] = useState<RateCap | undefined>(undefined);

  useEffect(() => {
    customPlanRateCapStore.setDefaultValues(
      rateRFQModalStore.dynamicForm?.customFields.rateCaps as CustomFieldControl<RateCap[]> | undefined,
    );
    return customPlanRateCapStore.resetInitialValues;
  }, []);

  const onNewRateCapDateStartChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    customPlanRateCapStore.setupInitialValidationsForNewCap();
    if (newRateCap) setNewRateCap({ ...newRateCap, startDate: e.target.value });
  };

  const onNewRateCapDateEndChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    customPlanRateCapStore.setupInitialValidationsForNewCap();
    if (newRateCap) setNewRateCap({ ...newRateCap, endDate: e.target.value });
  };

  const onNewRateCapPercentageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (newRateCap) setNewRateCap({ ...newRateCap, capPercentage: e.target.value });
    customPlanRateCapStore.validateNewRateCapPercentage(e.target.value);
  };

  const addRateCap = (): void => {
    customPlanRateCapStore.setupInitialValidationsForNewCap();
    setNewRateCap({
      startDate: '',
      endDate: '',
      capPercentage: '6',
      id: `rateCap_${uniqid()}`,
    });
  };

  const cancelNewRateCap = (): void => {
    setNewRateCap(undefined);
    customPlanRateCapStore.setupInitialValidationsForNewCap();
  };

  const saveRateCap = (): void => {
    if (customPlanRateCapStore.isNewRateCapValid(newRateCap)) {
      customPlanRateCapStore.saveRateCap(newRateCap);
      setNewRateCap(undefined);
      customPlanRateCapStore.setupInitialValidationsForNewCap();
      rateRFQModalStore.dynamicForm?.customFields.rateCaps?.updateValue(customPlanRateCapStore.getRateCaps());
    }
  };

  const removeRateCap = (): void => {
    customPlanRateCapStore.removeRateCap();
  };

  const classes = createStyles({ periodElementsAmount: convertedRateCaps.length });

  const newRateCapBlock = (): JSX.Element => {
    if (newRateCap) {
      return (
        <div className={classes.newRateCapWrapper}>
          <InputText
            className={classes.dateInput}
            name="newRateCapStart"
            label="Start of the Rate Cap"
            onChange={onNewRateCapDateStartChange}
            value={newRateCap.startDate}
            width="18rem"
            htmlType="date"
            validationPassed={!customPlanRateCapStore.validateField('newRateCapDateRange')}
            validationMessage={customPlanRateCapStore.validateField('newRateCapDateRange')}
          />
          <InputText
            className={classes.dateInput}
            name="newRateCapEnd"
            label="End of the Rate Cap"
            onChange={onNewRateCapDateEndChange}
            value={newRateCap.endDate}
            width="18rem"
            htmlType="date"
            validationPassed={!customPlanRateCapStore.validateField('newRateCapDateRange')}
            validationMessage={customPlanRateCapStore.validateField('newRateCapDateRange')}
          />
          <InputText
            className={classes.dateInput}
            name="newRateCapPercentage"
            label="Rate Cap"
            onChange={onNewRateCapPercentageChange}
            value={newRateCap.capPercentage}
            width="18rem"
            additionalOptions={{
              contributionType: 'percentage',
            }}
            validationPassed={!customPlanRateCapStore.validateField('newRateCapPercentage')}
            validationMessage={customPlanRateCapStore.validateField('newRateCapPercentage')}
          />
        </div>
      );
    }
    return <></>;
  };

  const rateCapCoverageBlock = (): JSX.Element => {
    if (convertedRateCaps.length === 0) return <></>;
    const rateCapPeriodsList = convertedRateCaps.map((rateCap) => (
      <TableRow key={rateCap.id} className={classes.rateCapPeriodsTableRow}>
        <TableCell className={classes.rateCapPeriodsTableCell}>
          {normDateToShortAmericanDate(rateCap.startDate)} - {normDateToShortAmericanDate(rateCap.endDate)}
        </TableCell>
        <TableCell className={classes.rateCapPeriodsTableCell}>{rateCap.capPercentage}%</TableCell>
      </TableRow>
    ));

    return (
      <div className={classes.rateCapPeriodsCapsTableWrapper}>
        <Table totalElements={convertedRateCaps.length} minHeight="auto">
          <TableHeader className={classes.rateCapPeriodsTableHeader}>
            <TableRow>
              <TableHeading className={classes.rateCapPeriodsTableHeading}>Coverage Period</TableHeading>
              <TableHeading className={classes.rateCapPeriodsTableHeading}>Rate Cap</TableHeading>
            </TableRow>
          </TableHeader>
          <TableBody className={classes.rateCapPeriodsTableBody}>{rateCapPeriodsList}</TableBody>
        </Table>
      </div>
    );
  };

  const newRateCapActionsBlock = (): JSX.Element => {
    return (
      <div className={classes.rateCapActionsSection}>
        {newRateCap ? (
          <>
            <Button
              leftIconProp={<Done />}
              type="text"
              color="custom"
              customColor={colors.green[600]}
              className={classes.rateCapActionButton}
              disabled={customPlanRateCapStore.hasInvalidFields()}
              onClick={(): void => {
                saveRateCap();
              }}
            >
              Save New Cap
            </Button>
            <Button
              leftIconProp={<NavigationClose />}
              type="text"
              color="custom"
              customColor={colors.red[500]}
              className={classes.rateCapActionButton}
              onClick={(): void => {
                cancelNewRateCap();
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              leftIconProp={<Add />}
              type="text"
              color="primary"
              className={classes.rateCapActionButton}
              disabled={customPlanRateCapStore.hasInvalidFields()}
              onClick={(): void => {
                addRateCap();
              }}
            >
              Add Rate Cap
            </Button>
            <Button
              leftIconProp={<Delete />}
              type="text"
              color="custom"
              customColor={colors.red[500]}
              className={classes.rateCapActionButton}
              disabled={convertedRateCaps.length === 0}
              onClick={(): void => {
                removeRateCap();
              }}
            >
              Remove Last Cap
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {rateCapCoverageBlock()}
      {newRateCapBlock()}
      {newRateCapActionsBlock()}
    </>
  );
});

export default PlanRateCap;
